import styled from 'styled-components';

// Container
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// Card header with icon (matching DatesTimesTab)
export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

export const CardIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fa4616;

  svg {
    width: 22px;
    height: 22px;
  }
`;

export const CardTitleGroup = styled.div``;

export const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
`;

export const CardSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  margin: 4px 0 0 0;
`;

// Section styling (for subsections within cards)
export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SectionDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) =>
    theme?.colors?.textGray?.secondary ||
    (theme?.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: -8px 0 16px 0;
`;

// Divider
export const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
  margin: 24px 0;
`;

// Form row - flexible layout
export const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || '1fr 1fr'};
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Single field wrapper
export const FieldWrapper = styled.div`
  margin-bottom: ${({ noMargin }) => (noMargin ? '0' : '20px')};
`;

// Field label for components that don't have built-in labels
export const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme.name === 'dark' ? '#E2E8F0' : '#374151')};
  margin-bottom: 8px;
`;

// Toggle row styles (matching AccountTab)
export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${({ theme }) =>
    theme?.colors?.background?.overlay ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)')};
  border-radius: 8px;
  margin-top: ${({ first }) => (first ? '0' : '12px')};

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-rows: auto auto;
    grid-template-areas:
      'icon label toggle'
      'description description description';
    gap: 6px 10px;
    align-items: center;
  }
`;

export const ToggleInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    display: contents;
  }
`;

export const ToggleIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fa4616;

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    grid-area: icon;
    width: 28px;
    height: 28px;
    border-radius: 6px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

export const ToggleText = styled.div`
  @media (max-width: 768px) {
    display: contents;
  }
`;

export const ToggleLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#0F172A'};

  @media (max-width: 768px) {
    grid-area: label;
    font-size: 15px;
    font-weight: 600;
  }
`;

export const ToggleDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) =>
    theme?.colors?.textGray?.secondary ||
    (theme?.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin-top: 2px;

  @media (max-width: 768px) {
    grid-area: description;
    margin-top: 0;
  }
`;

// Toggle switch
export const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    grid-area: toggle;
  }
`;

export const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background: linear-gradient(135deg, #fa4616, #ff6b3b);
  }

  &:checked + span:before {
    transform: translateX(22px);
  }
`;

export const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgb(226, 232, 240)'};
  transition: all 0.3s ease;
  border-radius: 26px;

  &:before {
    position: absolute;
    content: '';
    height: 20px;
    width: 20px;
    left: 3px;
    top: 3px;
    background-color: #fff;
    transition: all 0.3s ease;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

// Collapsible section
export const CollapsibleHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  font: inherit;
  text-align: left;

  &:hover {
    opacity: 0.8;
  }
`;

export const CollapsibleIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'};
  color: ${({ theme }) => (theme?.name === 'dark' ? '#94A3B8' : '#64748B')};
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const CollapsibleContent = styled.div`
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  margin-top: 20px;
`;

// Loading state
export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  font-size: 16px;
`;

// Divisions grid (themed version)
export const DivisionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 12px;
`;

export const DivisionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: ${({ theme, checked }) =>
    checked
      ? theme?.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250, 70, 22, 0.08)'
      : theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${({ theme, checked }) =>
      checked
        ? '#fa4616'
        : theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ checked }) => (checked ? '#fa4616' : '#cbd5e1')};
  }
`;

export const DivisionCheckbox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${({ checked }) => (checked ? '#fa4616' : '#cbd5e1')};
  background: ${({ checked }) => (checked ? '#fa4616' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    width: 12px;
    height: 12px;
    color: white;
  }
`;

export const DivisionLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0F172A')};
`;

// Empty state
export const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 8px;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#94A3B8' : '#64748B')};
  font-size: 14px;
`;

// Series tags
export const SeriesTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

export const SeriesTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  border: 1px solid #fa4616;
  border-radius: 20px;
  font-size: 13px;
  color: #fa4616;
`;

export const RemoveTagButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #fa4616;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

// Expanded section revealed when a related toggle is enabled
export const ExpandedSection = styled.div`
  margin-top: 16px;
  padding: 20px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#f8fafc'};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 8px;
`;

export const InfoBox = styled.div`
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(59, 130, 246, 0.1)' : '#eff6ff'};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#bfdbfe'};
  border-radius: 6px;
  margin-bottom: 16px;
`;

export const InfoText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#93c5fd' : '#1e40af')};
`;

export const SectionLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#e5e7eb' : '#374151')};
  margin-bottom: 8px;
`;

export const OptionalBadge = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#6b7280' : '#9ca3af')};
  margin-left: 6px;
`;
